/**
 * Thin wrapper around the underlying date library (currently date-fns).
 *
 * This is the ONLY module in the app that should import a date library.
 * Everything else must go through the helpers exported here, so that swapping
 * the library in future is a single-file change.
 */
import { format } from 'date-fns';
import { enUS, id, zhCN, th } from 'date-fns/locale';

// The underlying library has no global-locale singleton, so we keep the
// currently-selected locale here and apply it when formatting.
const LOCALE_MAP = {
	'en-US': enUS,
	'en-MY': enUS,
	'id-ID': id,
	'zh-CN': zhCN,
	'th-TH': th,
};

// Matches moment's legacy 'LL HH:mm:ss' (e.g. "July 6, 2026 14:30:00").
const DEFAULT_DATE_TIME_FORMAT = 'MMMM d, yyyy HH:mm:ss';

let currentLocale = enUS;

export const setLocale = (localeKey) => {
	currentLocale = LOCALE_MAP[localeKey] || enUS;
};

// A timezone-naive string ("2026-07-06T14:30:00") is interpreted as UTC by the
// legacy moment.utc behaviour but as local time by the native Date parser, so
// append 'Z' to preserve it. Strings that already carry a zone/offset and
// non-string inputs are passed through untouched.
const parseAsUtc = (date) => {
	if (typeof date === 'string' && !/[zZ]|(?:[+-]\d{2}:?\d{2}$)/.test(date)) {
		return new Date(`${date}Z`);
	}
	return new Date(date);
};

export const formatDefaultDateTime = (date, utc = true) => {
	const parsedDate = utc ? parseAsUtc(date) : new Date(date);
	// The library always formats in the local timezone, matching moment's .local().
	return format(parsedDate, DEFAULT_DATE_TIME_FORMAT, { locale: currentLocale });
};
